import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Contact.scss";

const initialFormData = {
    name: "",
    email: "",
    topic: "",
    message: "",
};

const Contact = () => {
    const [formData, setFormData] = useState(initialFormData);
    const [status, setStatus] = useState({ type: "", message: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submittingRef.current) return;

        const name = formData.name.trim();
        const email = formData.email.trim();
        const message = formData.message.trim();

        if (!name || !email || !formData.topic || !message) {
            setStatus({
                type: "error",
                message: "Please complete all fields before sending.",
            });
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setStatus({ type: "sending", message: "Sending your message…" });

        const payload = new URLSearchParams({
            name,
            email,
            message: `Enquiry: ${formData.topic}\n\n${message}`,
        });

        try {
            const response = await fetch(
                "https://techadive.com/send_mail.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: payload,
                }
            );

            const result = await response.text();

            if (!response.ok || result.trim() !== "success") {
                throw new Error("Message submission failed");
            }

            setStatus({
                type: "success",
                message: "Your message has been sent. Thank you for getting in touch.",
            });
            setFormData({ ...initialFormData });
        } catch {
            setStatus({
                type: "error",
                message:
                    "We couldn’t confirm that your message was sent. Please try again or email info@techadive.com.",
            });
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <section
                className="contact-page__hero"
                aria-labelledby="contact-title"
            >
                <div className="contact-page__container">
                    <p className="contact-page__eyebrow">Contact Techadive</p>

                    <h1 id="contact-title">
                        Let’s Start a <span>Conversation.</span>
                    </h1>

                    <p className="contact-page__lead">
                        Have a digital project in mind, a partnership to explore,
                        or a question about our ventures? Tell us what you’re
                        working on.
                    </p>
                </div>
            </section>

            <section
                className="contact-page__section"
                aria-labelledby="contact-form-title"
            >
                <div className="contact-page__container contact-page__layout">
                    <div className="contact-page__form-panel">
                        <h2 id="contact-form-title">Tell Us About Your Idea</h2>

                        <p className="contact-page__form-intro">
                            All fields are required.
                        </p>

                        <form
                            className="contact-page__form"
                            onSubmit={handleSubmit}
                            aria-labelledby="contact-form-title"
                        >
                            <fieldset disabled={isSubmitting}>
                                <div className="contact-page__field">
                                    <label htmlFor="contact-name">Your name</label>
                                    <input
                                        id="contact-name"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        maxLength={120}
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="contact-email">Email address</label>
                                    <input
                                        id="contact-email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        maxLength={254}
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="contact-topic">
                                        What would you like to discuss?
                                    </label>
                                    <select
                                        id="contact-topic"
                                        name="topic"
                                        value={formData.topic}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>
                                            Select an enquiry topic
                                        </option>
                                        <option value="Technology Projects">
                                            Technology Projects
                                        </option>
                                        <option value="Invest Africa & Partnerships">
                                            Invest Africa &amp; Partnerships
                                        </option>
                                        <option value="Our Ventures">Our Ventures</option>
                                        <option value="General Enquiry">General Enquiry</option>
                                    </select>
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="contact-message">Your message</label>
                                    <textarea
                                        id="contact-message"
                                        name="message"
                                        rows={7}
                                        maxLength={5000}
                                        placeholder="Share your goals and how we can help."
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <p className="contact-page__privacy">
                                    Read our <Link to="/privacy-policy">Privacy Policy</Link>{" "}
                                    for information about how we handle your details.
                                </p>

                                <button
                                    className="contact-page__submit"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Sending…" : "Send Message"}
                                    <span aria-hidden="true">↗</span>
                                </button>
                            </fieldset>

                            <p
                                className={`contact-page__status ${
                                    status.type ? `contact-page__status--${status.type}` : ""
                                }`}
                                role="status"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                {status.message}
                            </p>
                        </form>
                    </div>

                    <aside
                        className="contact-page__details"
                        aria-labelledby="contact-details-title"
                    >
                        <p className="contact-page__eyebrow">Get in Touch</p>
                        <h2 id="contact-details-title">Connect Directly</h2>

                        <address className="contact-page__address">
                            <div className="contact-page__detail">
                                <h3>Email</h3>
                                <a href="mailto:info@techadive.com">
                                    info@techadive.com
                                </a>
                            </div>

                            <div className="contact-page__detail">
                                <h3>Phone</h3>
                                <a href="tel:+48791285559">+48 791 285 559</a>
                            </div>

                            <div className="contact-page__detail">
                                <h3>Locations</h3>
                                <p>Nairobi, Kenya</p>
                                <p>Warsaw, Poland</p>
                            </div>
                        </address>

                        <a
                            className="contact-page__social"
                            href="https://www.linkedin.com/company/tech-a-dive/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Follow Techadive on LinkedIn
                            <span aria-hidden="true">↗</span>
                            <span className="contact-page__sr-only">
                {" "}
                                (opens in a new tab)
              </span>
                        </a>
                    </aside>
                </div>
            </section>
        </div>
    );
};

export default Contact;